import { useEffect, useState, useRef } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import axios from "axios";
import { BASE_URL } from "../api";
import dayjs from "dayjs";
import MessageToster from "../component/MessageToster";
import Loader from "../component/Loader";
import { useLocation, useNavigate } from "react-router-dom";
import { useHandleError } from "../hooks/useHandleError";
import { getApiHeaders, getFullMediaUrl, getAdminAttachmentUrl } from "../utils/helpers";
import CommonTable from "../component/CommonTable";

export default function TicketInformation() {
    const navigate = useNavigate();
    const { search } = useLocation();
    const queryParams = new URLSearchParams(search);
    const id = queryParams.get("id");
    const handleError = useHandleError();

    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [message, setMessages] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [chatInput, setChatInput] = useState("");
    const [chatFile, setChatFile] = useState(null);
    const fileUpload = useRef(null);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [message]);

    const getTicketDetails = async () => {
        setLoading(true);
        const bodyData = { mode: "", ticket_id: Number(id) };

        try {
            const response = await axios.post(
                `${BASE_URL}/support/get_ticket_details_by_ticket_id`,
                bodyData,
                { headers: getApiHeaders() }
            );

            if (response.data.code === 200) {
                let ticketData = response.data.message[0] || null;
                if (ticketData && (ticketData.Attachment || ticketData.attachment)) {
                    ticketData.Attachment = getFullMediaUrl(ticketData.Attachment || ticketData.attachment);
                }
                setTicket(ticketData);
            } else {
                MessageToster("error", response.data.message);
            }
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const createdDate = ticket?.support_ondate ? dayjs(ticket.support_ondate).format("DD-MM-YYYY") : "-";
    const closedDate = ticket?.closed_date ? dayjs(ticket.closed_date).format("DD-MM-YYYY") : "-";

    const getMessages = async () => {
        const bodyData = { mode: "", ticket_id: Number(id) };

        try {
            const response = await axios.post(
                `${BASE_URL}/support/get_message`,
                bodyData,
                { headers: getApiHeaders() }
            );

            if (response.data.code === 200) {
                const msgData = response.data.message;
                const messagesArray = Array.isArray(msgData) ? msgData.flat() : (msgData ? [msgData] : []);

                const mappedMessages = messagesArray.map((msg) => {
                    const attach = msg.Attachment || msg.attachment;
                    const isUserMsg = String(msg.sender_type || "").trim().toLowerCase() === "user";
                    const finalUrl = attach
                        ? (isUserMsg ? getFullMediaUrl(attach) : getAdminAttachmentUrl(attach))
                        : null;
                    return {
                        ...msg,
                        finalAttachmentUrl: finalUrl,
                    };
                });
                setMessages(mappedMessages);
            } else {
                MessageToster("error", response.data.message);
            }
        } catch (error) {
            handleError(error);
        }
    };

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const bodyData = { mode: "", ticket_id: Number(id) };

            const ticketPromise = axios.post(
                `${BASE_URL}/support/get_ticket_details_by_ticket_id`,
                bodyData,
                { headers: getApiHeaders() }
            );
            const messagesPromise = axios.post(
                `${BASE_URL}/support/get_message`,
                bodyData,
                { headers: getApiHeaders() }
            );

            const [ticketRes, messagesRes] = await Promise.all([ticketPromise, messagesPromise]);

            if (ticketRes.data.code === 200) {
                let fetchedTicket = ticketRes.data.message[0] || null;
                if (fetchedTicket && (fetchedTicket.Attachment || fetchedTicket.attachment)) {
                    fetchedTicket.Attachment = getFullMediaUrl(fetchedTicket.Attachment || fetchedTicket.attachment);
                }
                setTicket(fetchedTicket);
            } else {
                MessageToster("error", ticketRes.data.message);
            }

            if (messagesRes.data.code === 200) {
                const msgData = messagesRes.data.message;
                const messagesArray = Array.isArray(msgData) ? msgData.flat() : (msgData ? [msgData] : []);

                const mappedMessages = messagesArray.map((msg) => {
                    const attach = msg.Attachment || msg.attachment;
                    const isUserMsg = String(msg.sender_type || "").trim().toLowerCase() === "user";
                    const finalUrl = attach
                        ? (isUserMsg ? getFullMediaUrl(attach) : getAdminAttachmentUrl(attach))
                        : null;
                    return {
                        ...msg,
                        finalAttachmentUrl: finalUrl,
                    };
                });
                setMessages(mappedMessages);
            }
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

            if (!allowedTypes.includes(file.type)) {
                MessageToster("error", "Invalid file type. Only jpeg, jpg, png, and pdf are allowed.");
                e.target.value = null;
                setChatFile(null);
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                MessageToster("error", "File size must be less than 5MB.");
                e.target.value = null;
                setChatFile(null);
                return;
            }
            setChatFile(file);
        } else {
            setChatFile(null);
        }
    };

    const sendMessage = async () => {
        if (!chatInput.trim() && !chatFile) return;
        setIsSending(true);
        try {
            const formData = new FormData();
            formData.append("ticket_id", String(id));
            formData.append("note", chatInput || "");
            formData.append("attachment", chatFile || "");

            const response = await axios.post(`${BASE_URL}/support/ticket_message`, formData, {
                headers: getApiHeaders(true),
            });

            if (response.data.code === 200) {
                setChatInput("");
                setChatFile(null);
                if (fileUpload.current) fileUpload.current.value = "";
                getMessages();
            } else {
                MessageToster("error", response.data.message);
            }
        } catch (error) {
            handleError(error);
        } finally {
            setIsSending(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchInitialData();
        }
    }, [id]);

    return (
        <>
            <div id="layout-wrapper">
                {loading && <Loader />}
                <Topbar />
                <Sidebar />
                <div className="main-content">
                    <div className="page-content">
                        <div className="container-fluid">
                            {/* Header with Back button */}
                            <div className="row">
                                <div className="col-12">
                                    <div className="page-title-box d-flex align-items-center justify-content-between">
                                        <h2 className="fw-700 mb-0">Tickets Information</h2>
                                        <button
                                            className="btn btn-primary d-flex align-items-center rounded-3 shadow-sm"
                                            onClick={() => navigate(-1)}
                                        >
                                            <i className="fa-solid fa-arrow-left me-2"></i>Back
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-4 mb-4">
                                {/* Left Card: Ticket Details */}
                                <div className="col-xl-6 col-lg-12">
                                    <div className="card h-100 shadow-sm border-0 rounded-4">
                                        <div className="card-body p-4">
                                            <div className="table-responsive">
                                                <CommonTable
                                                    hideHeader={true}
                                                    columns={[
                                                        {
                                                            accessor: "label",
                                                            className: "border-end py-3 text-muted fw-semibold",
                                                            render: (row) => <h6 style={{ whiteSpace: "nowrap" }} className="mb-0 font-size-14">{row.label}</h6>,
                                                        },
                                                        {
                                                            accessor: "value",
                                                            className: "py-3",
                                                            render: (row) => <h6 className="mb-0 font-size-14">{row.value || "-"}</h6>,
                                                        },
                                                    ]}
                                                    data={[
                                                        { label: "Ticket Number", value: ticket?.ticketno },
                                                        { label: "Subject", value: ticket?.title },
                                                        ...(ticket?.priority ? [{ label: "Priority", value: ticket?.priority }] : []),
                                                        { label: "Description", value: ticket?.note },
                                                        { label: "Status", value: ticket?.status },
                                                        { label: "Department", value: ticket?.department },
                                                        { label: "Date", value: createdDate },
                                                        { label: "Closed", value: closedDate },
                                                        ...(ticket?.Attachment ? [{
                                                            label: "Attachment",
                                                            value: (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-soft-primary d-inline-flex align-items-center gap-1"
                                                                    data-bs-toggle="modal"
                                                                    data-bs-target="#viewImageModal"
                                                                    onClick={() => setSelectedImage(ticket.Attachment)}
                                                                >
                                                                    <i className="fa-regular fa-image me-1"></i>
                                                                    <span>View Attachment</span>
                                                                </button>
                                                            )
                                                        }] : [])
                                                    ]}
                                                    disablePagination={true}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Card: Chat History */}
                                <div className="col-xl-6 col-lg-12">
                                    <div className="card h-100 shadow-sm border-0 rounded-4">
                                        <div className="card-body p-3 p-md-4 d-flex flex-column" style={{ minHeight: "550px" }}>
                                            <h4 className="border-bottom pb-3 fs-5 fw-bold mb-3">
                                                Chat History
                                            </h4>

                                            {/* Messages container */}
                                            <div
                                                className="chat-history-container flex-grow-1 overflow-auto pe-2"
                                                style={{ height: "430px", maxHeight: "430px" }}
                                            >
                                                {message.length === 0 ? (
                                                    <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted py-5">
                                                        <i className="fa-regular fa-comments fs-1 mb-2 opacity-50"></i>
                                                        <p className="mb-0">No messages yet. Send a message below.</p>
                                                    </div>
                                                ) : (
                                                    message.map((msg, index) => {
                                                        const isUser = String(msg.sender_type || "").trim().toLowerCase() === "user";
                                                        return (
                                                            <div
                                                                className={`d-flex mb-3 ${isUser ? "justify-content-end" : "justify-content-start"
                                                                    } align-items-end gap-2`}
                                                                key={msg.id || index}
                                                            >
                                                                {/* Admin avatar on left */}
                                                                {!isUser && (
                                                                    <div
                                                                        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 bg-secondary text-white shadow-sm"
                                                                        style={{ width: "34px", height: "34px" }}
                                                                        title="Support Admin"
                                                                    >
                                                                        <i className="fa-solid fa-headset" style={{ fontSize: "13px" }}></i>
                                                                    </div>
                                                                )}

                                                                <div
                                                                    className={`d-flex flex-column ${isUser ? "align-items-end" : "align-items-start"
                                                                        }`}
                                                                    style={{ maxWidth: "75%" }}
                                                                >
                                                                    <div
                                                                        className="p-2 shadow-sm"
                                                                        style={{
                                                                            borderRadius: isUser
                                                                                ? "12px 12px 4px 12px"
                                                                                : "12px 12px 12px 4px",
                                                                            fontSize: "14px",
                                                                            backgroundColor: isUser ? "#405189" : "var(--bs-light, #f3f5f8)",
                                                                            color: isUser ? "#ffffff" : "var(--bs-body-color, #1a202c)",
                                                                            border: isUser ? "none" : "1px solid var(--bs-border-color, #e2e8f0)",
                                                                        }}
                                                                    >
                                                                        {msg.message_text && msg.message_text !== "null" && (
                                                                            <div
                                                                                style={{
                                                                                    whiteSpace: "pre-wrap",
                                                                                    wordBreak: "break-word",
                                                                                }}
                                                                            >
                                                                                {msg.message_text}
                                                                            </div>
                                                                        )}

                                                                        {msg.finalAttachmentUrl && (
                                                                            <div
                                                                                className="mt-2 p-2 rounded-3 d-flex align-items-center gap-2"
                                                                                style={{
                                                                                    cursor: "pointer",
                                                                                    backgroundColor: isUser
                                                                                        ? "rgba(255,255,255,0.12)"
                                                                                        : "rgba(0,0,0,0.05)",
                                                                                    border: `1px solid ${isUser
                                                                                        ? "rgba(255,255,255,0.2)"
                                                                                        : "rgba(0,0,0,0.1)"
                                                                                        }`,
                                                                                }}
                                                                                data-bs-toggle="modal"
                                                                                data-bs-target="#viewImageModal"
                                                                                onClick={() => setSelectedImage(msg.finalAttachmentUrl)}
                                                                            >
                                                                                <div
                                                                                    className="rounded d-flex align-items-center justify-content-center flex-shrink-0"
                                                                                    style={{
                                                                                        width: "36px",
                                                                                        height: "36px",
                                                                                        backgroundColor: isUser
                                                                                            ? "rgba(255,255,255,0.2)"
                                                                                            : "rgba(0,0,0,0.08)",
                                                                                    }}
                                                                                >
                                                                                    <i className="fa-regular fa-image" style={{ fontSize: "16px" }}></i>
                                                                                </div>
                                                                                <div
                                                                                    className="flex-grow-1 overflow-hidden"
                                                                                    style={{ minWidth: "150px", maxWidth: "150px" }}
                                                                                >
                                                                                    <div
                                                                                        className="text-truncate fw-semibold"
                                                                                        style={{ fontSize: "13px", lineHeight: "1.2" }}
                                                                                    >
                                                                                        {msg.document_file_name || msg.Attachment || msg.attachment || "Attachment"}
                                                                                    </div>
                                                                                    <div
                                                                                        style={{
                                                                                            fontSize: "11px",
                                                                                            opacity: 0.8,
                                                                                            marginTop: "2px",
                                                                                        }}
                                                                                    >
                                                                                        Click to view
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    <small className="text-muted mt-1 px-1" style={{ fontSize: "11px" }}>
                                                                        {dayjs(msg.ondate).format("DD MMM YYYY, HH:mm")}
                                                                    </small>
                                                                </div>

                                                                {/* User avatar on right */}
                                                                {isUser && (
                                                                    <div
                                                                        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 shadow-sm"
                                                                        style={{
                                                                            width: "34px",
                                                                            height: "34px",
                                                                            backgroundColor: "#405189",
                                                                            color: "#ffffff",
                                                                        }}
                                                                        title="You"
                                                                    >
                                                                        <i className="fa-solid fa-user" style={{ fontSize: "13px" }}></i>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })
                                                )}
                                                <div ref={messagesEndRef} />
                                            </div>

                                            {/* Chat input footer */}
                                            <div className="mt-3 pt-3 border-top">
                                                {chatFile && (
                                                    <div className="mb-2">
                                                        <span className="badge bg-primary-subtle text-primary border border-primary p-2 fs-13 d-inline-flex align-items-center rounded-3">
                                                            <i className="fa-solid fa-file me-2"></i> {chatFile.name}
                                                            <i
                                                                className="fa-solid fa-times ms-3"
                                                                style={{ cursor: "pointer" }}
                                                                onClick={() => {
                                                                    setChatFile(null);
                                                                    if (fileUpload.current) fileUpload.current.value = "";
                                                                }}
                                                                title="Remove Attachment"
                                                            ></i>
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="input-group">
                                                    <input
                                                        type="file"
                                                        style={{ display: "none" }}
                                                        onChange={handleFileChange}
                                                        ref={fileUpload}
                                                        accept=".jpg,.jpeg,.png,.pdf"
                                                    />
                                                    <button
                                                        className="btn btn-light border"
                                                        type="button"
                                                        onClick={() => fileUpload.current?.click()}
                                                        title="Attach File"
                                                    >
                                                        <i className="fa-solid fa-paperclip"></i>
                                                    </button>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Type your message..."
                                                        value={chatInput}
                                                        onChange={(e) => setChatInput(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                e.preventDefault();
                                                                sendMessage();
                                                            }
                                                        }}
                                                    />
                                                    <button
                                                        className="btn btn-primary"
                                                        type="button"
                                                        onClick={sendMessage}
                                                        disabled={isSending || (!chatInput.trim() && !chatFile)}
                                                    >
                                                        {isSending ? (
                                                            <span
                                                                className="spinner-border spinner-border-sm"
                                                                role="status"
                                                                aria-hidden="true"
                                                            ></span>
                                                        ) : (
                                                            <i className="fa-solid fa-paper-plane"></i>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Image / Document Modal */}
                    <div
                        className="modal fade"
                        id="viewImageModal"
                        tabIndex="-1"
                        aria-labelledby="viewImageModalLabel"
                        aria-hidden="true"
                    >
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content rounded-4 border-0 shadow">
                                <div className="modal-header">
                                    <h5 className="modal-title fw-bold" id="viewImageModalLabel">
                                        Attachment Document
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        data-bs-dismiss="modal"
                                        aria-label="Close"
                                    />
                                </div>
                                <div className="modal-body text-center p-4">
                                    {selectedImage ? (
                                        <>
                                            {selectedImage.toLowerCase().split("?")[0].endsWith(".pdf") ? (
                                                <div className="py-4">
                                                    <i className="fa-solid fa-file-pdf text-danger fs-1 mb-3"></i>
                                                    <p className="fw-semibold mb-3">PDF Document</p>
                                                    <a
                                                        href={selectedImage}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="btn btn-primary btn-sm"
                                                    >
                                                        <i className="fa-solid fa-arrow-up-right-from-square me-1"></i> Open PDF
                                                    </a>
                                                </div>
                                            ) : (
                                                <img
                                                    src={selectedImage}
                                                    alt="Attachment"
                                                    className="img-fluid rounded-3 mb-3 shadow-sm"
                                                    style={{ maxHeight: "70vh", objectFit: "contain" }}
                                                />
                                            )}
                                            <br />
                                            <button
                                                className="btn btn-sm btn-success rounded-3 mt-2"
                                                onClick={() => window.open(selectedImage, "_blank")}
                                            >
                                                <i className="fa-solid fa-download me-1"></i> Download
                                            </button>
                                        </>
                                    ) : (
                                        <p className="text-muted">No image available</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <Footer />
                </div>
            </div>
        </>
    );
}
